import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, type Variants } from 'framer-motion';
import Layout from './Layout';
import { getConstellationDetail } from './services/api';
import { Button, Card, LoadingSpinner, GalleryZoomImage } from './components';
import { getElementIcon, getAnimalEmoji } from './utils/constellationData';
import type { Constellation } from './utils/constellationData';

const ResultPage: React.FC = () => {
  const { constellationId } = useParams<{ constellationId: string }>();
  const navigate = useNavigate();

  const [constellation, setConstellation] = useState<Constellation | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  // 内联轮播：0=插画  1=塑像
  const [slideIndex, setSlideIndex] = useState(0);
  const touchStartXRef = useRef<number | null>(null);

  // 灯箱
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lightboxTop, setLightboxTop] = useState(0);
  const openLightbox = (index: number) => {
    setLightboxTop(window.scrollY || document.documentElement.scrollTop || 0);
    setLightboxIndex(index);
  };
  const closeLightbox = () => setLightboxIndex(null);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') closeLightbox(); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [lightboxIndex]);

  useEffect(() => {
    document.body.classList.add('page-enter');
    return () => { document.body.classList.remove('page-enter'); };
  }, []);

  useEffect(() => {
    const fetch = async () => {
      if (!constellationId) { setError('未找到星宿ID'); setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        const data = await getConstellationDetail(constellationId);
        setConstellation(data);
      } catch {
        setError('获取星宿详情失败，请稍后重试');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [constellationId]);

  const handleShare = async () => {
    if (!constellation) return;
    const shareText = `我的本命星宿是【${constellation.name}】— ${constellation.description}。快来测算你的守护星宿！`;
    const shareUrl = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: `${constellation.name} — 二十八星宿测算`, text: shareText, url: shareUrl }); } catch { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
      } catch {
        window.prompt('复制以下内容分享给朋友：', `${shareText}\n${shareUrl}`);
      }
    }
  };

  const handleJumpToShop = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    window.open(`https://shop.dnaps.com/pages/goods/goods_list/index?searchValue=${encodeURIComponent('星宿')}`, '_blank');
  };

  const pageVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut', staggerChildren: 0.15 } },
  };
  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-brand-primary to-brand-secondary flex items-center justify-center px-4">
          <LoadingSpinner size="lg" message="正在演算星辰轨迹..." />
        </div>
      </Layout>
    );
  }

  if (error || !constellation) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-brand-primary to-brand-secondary flex items-center justify-center px-4">
          <Card variant="glass" className="max-w-lg w-full text-center" padding>
            <svg className="w-16 h-16 mx-auto mb-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <h1 className="text-2xl font-bold text-brand-accent font-serif-chinese mb-3">未找到星宿信息</h1>
            <p className="text-neutral-300 mb-8">{error || '星宿数据不存在'}</p>
            <Button onClick={() => navigate('/')} size="lg">返回首页重新测算</Button>
          </Card>
        </div>
      </Layout>
    );
  }

  const data = constellation;
  const personalityIcons = ['🚀', '🎯', '🛡️'];
  const personalityLabels = ['属性特质', '动物形象', '文化特征'];

  const GROUP_MAP: Record<string, string> = {
    jiao: '东方青龙', kang: '东方青龙', di: '东方青龙', fang: '东方青龙',
    xin: '东方青龙', wei: '东方青龙', ji: '东方青龙',
    dou: '北方玄武', niu: '北方玄武', nv: '北方玄武', xu: '北方玄武',
    wei2: '北方玄武', shi: '北方玄武', bi: '北方玄武',
    kui: '西方白虎', lou: '西方白虎', wei3: '西方白虎', mao: '西方白虎',
    bi2: '西方白虎', zi: '西方白虎', shen: '西方白虎',
    jing: '南方朱雀', gui: '南方朱雀', liu: '南方朱雀', xing: '南方朱雀',
    zhang: '南方朱雀', yi: '南方朱雀', zhen: '南方朱雀',
  };
  const groupName = GROUP_MAP[data.id] || '';

  const artworkSrc = data.imageUrl || `/images/constellations/${data.id}.webp`;
  const sculptureSrc = data.sculptureImageUrl || null;
  const hasScuplture = !!sculptureSrc;

  // 灯箱图片：插画 + 塑像（如有）
  const lightboxImages: { src: string; alt: string }[] = [
    { src: artworkSrc, alt: `${data.name} 插画` },
    ...(sculptureSrc ? [{ src: sculptureSrc, alt: `${data.name} 玉皇庙彩塑` }] : []),
  ];

  // 内联轮播触摸
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartXRef.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;
    if (dx < -50 && hasScuplture && slideIndex === 0) setSlideIndex(1);
    if (dx > 50 && slideIndex === 1) setSlideIndex(0);
  };

  return (
    <Layout>
      <>
        <motion.div
          className="min-h-screen bg-gradient-to-b from-brand-primary to-brand-secondary px-4 pb-4 md:px-8 md:pb-8 -mt-6 md:-mt-8 -mx-4 relative overflow-hidden"
          initial="hidden"
          animate="visible"
          variants={pageVariants}
          role="main"
          aria-label={`${data.name} - 星宿测算结果`}
        >
          {/* 星光背景 */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
            <div className="star-field" />
          </div>

          <div className="relative max-w-3xl mx-auto pt-2">

            {/* ===== 标签 + 名称 + 简介 ===== */}
            <motion.div className="text-center mb-6" variants={itemVariants}>
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/30 text-brand-accent text-xs">
                  <span>⭐</span>
                  <span>本命星宿</span>
                </div>
                {groupName && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-accent/15 border border-brand-accent/30 text-brand-accent text-xs">
                    <span>{groupName}</span>
                  </div>
                )}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-brand-accent mb-3 font-comic-chinese tracking-wider">
                {data.name}
              </h1>
              <p className="text-neutral-300 text-base md:text-lg max-w-md mx-auto leading-relaxed">
                {data.description}
              </p>
            </motion.div>

            {/* ===== 大图轮播：插画 ↔ 塑像 ===== */}
            <motion.figure className="flex flex-col items-center mb-8" variants={itemVariants} role="figure" aria-label="星宿图片">
              {/* 轮播容器 */}
              <div
                className="relative w-full max-w-md rounded-2xl overflow-hidden border border-brand-accent/30 shadow-gold-sm"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                {/* 滑动轨道 */}
                <div
                  className="flex"
                  style={{
                    transform: `translateX(-${slideIndex * 100}%)`,
                    transition: 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    willChange: 'transform',
                  }}
                >
                  {/* 插画 */}
                  <div
                    className="w-full flex-shrink-0 bg-black/25 cursor-zoom-in"
                    onClick={() => openLightbox(0)}
                    role="button"
                    tabIndex={0}
                    aria-label="放大查看本命星宿插画"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openLightbox(0);
                      }
                    }}
                  >
                    <img
                      src={artworkSrc}
                      alt={`${data.name} 新中式插画`}
                      className="w-full aspect-square object-contain"
                      loading="eager"
                    />
                  </div>
                  {/* 塑像 */}
                  {sculptureSrc && (
                    <div
                      className="w-full flex-shrink-0 bg-black/25 cursor-zoom-in"
                      onClick={() => openLightbox(1)}
                      role="button"
                      tabIndex={0}
                      aria-label="放大查看玉皇庙彩塑"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openLightbox(1);
                        }
                      }}
                    >
                      <img
                        src={sculptureSrc}
                        alt={`${data.name} 玉皇庙彩塑`}
                        className="w-full aspect-square object-contain"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>

                {/* 底部渐变遮罩：标签 + 指示点 */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent px-4 py-3 flex items-end justify-between">
                  <span className="text-brand-accent text-sm font-comic-chinese">
                    {slideIndex === 0 ? '新中式插画' : '玉皇庙彩塑'}
                  </span>
                  {hasScuplture && (
                    <div className="flex items-center gap-1.5" aria-label={`第 ${slideIndex + 1} 张，共 2 张`}>
                      <span className={`block w-1.5 h-1.5 rounded-full transition-colors duration-200 ${slideIndex === 0 ? 'bg-brand-accent' : 'bg-white/40'}`} />
                      <span className={`block w-1.5 h-1.5 rounded-full transition-colors duration-200 ${slideIndex === 1 ? 'bg-brand-accent' : 'bg-white/40'}`} />
                    </div>
                  )}
                </div>

                {/* 左右点击切换区（桌面端） */}
                {hasScuplture && slideIndex === 0 && (
                  <button
                    className="absolute right-0 top-0 bottom-8 w-12 flex items-center justify-end pr-2 bg-transparent border-0 cursor-pointer opacity-0 hover:opacity-100 transition-opacity"
                    onClick={() => setSlideIndex(1)}
                    aria-label="查看塑像"
                  >
                    <svg className="w-6 h-6 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
                {slideIndex === 1 && (
                  <button
                    className="absolute left-0 top-0 bottom-8 w-12 flex items-center justify-start pl-2 bg-transparent border-0 cursor-pointer opacity-0 hover:opacity-100 transition-opacity"
                    onClick={() => setSlideIndex(0)}
                    aria-label="查看插画"
                  >
                    <svg className="w-6 h-6 text-white drop-shadow" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
              </div>

              {/* 滑动提示 */}
              <div className="flex items-center justify-center gap-4 mt-2.5">
                {hasScuplture && (
                  <span className="text-neutral-600 text-xs">← 滑动切换插画 / 塑像 →</span>
                )}
              </div>
            </motion.figure>

            {/* ===== 守护神寄语 ===== */}
            <motion.div variants={itemVariants}>
              <Card variant="highlight" hoverable shadow="gold" className="mb-10">
                <h2 className="text-lg md:text-xl font-bold text-brand-accent font-serif-chinese mb-3 flex items-center gap-2">
                  <span aria-hidden="true">✨</span>守护神寄语
                </h2>
                <p className="text-base text-neutral-200 leading-relaxed">
                  {data.guardianMessage}
                </p>
              </Card>
            </motion.div>

            {/* ===== 性格图谱 ===== */}
            <motion.section aria-labelledby="personality-heading" className="mb-10 md:mb-12" variants={itemVariants}>
              <h2 id="personality-heading" className="text-xl md:text-2xl font-bold text-brand-accent mb-5 text-center font-serif-chinese">
                性格图谱
              </h2>
              <div className="grid grid-cols-3 gap-3 md:gap-5">
                {data.personalityGraph.map((keyword, index) => (
                  <motion.article
                    key={index}
                    className="bg-neutral-900/50 backdrop-blur-sm rounded-2xl p-4 md:p-6 border border-neutral-800 card-hover text-center"
                    variants={itemVariants}
                    whileHover={{ scale: 1.03 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    aria-label={`性格特征：${keyword}`}
                  >
                    <div className="text-2xl md:text-3xl mb-3" aria-hidden="true">{personalityIcons[index]}</div>
                    <h3 className="text-sm md:text-base font-semibold text-brand-accent-light mb-1 font-comic-chinese">{keyword}</h3>
                    <p className="text-neutral-400 text-xs md:text-sm">{personalityLabels[index]}</p>
                  </motion.article>
                ))}
              </div>
            </motion.section>

            {/* ===== 星宿特质：属性特质 / 动物形象（文字介绍）===== */}
            <motion.section aria-labelledby="attributes-heading" className="mb-10 md:mb-12" variants={itemVariants}>
              <h2 id="attributes-heading" className="text-xl md:text-2xl font-bold text-brand-accent mb-5 text-center font-serif-chinese">
                星宿特质
              </h2>
              <div className="space-y-4">
                {/* 属性特质：取 characteristics 字段文字介绍（字段内容待补充，空值时显示占位） */}
                <motion.div variants={itemVariants}>
                  <Card variant="glass" shadow="sm">
                    <h3 className="text-base md:text-lg font-bold text-brand-accent-light mb-2 font-comic-chinese flex items-center gap-2">
                      <span aria-hidden="true">{getElementIcon(data.element)}</span>属性特质
                    </h3>
                    <p className="text-sm md:text-base text-neutral-200 leading-relaxed">
                      {data.characteristics || '（属性特质内容待补充）'}
                    </p>
                  </Card>
                </motion.div>
                {/* 动物形象：取 origin_statue_feature 字段，展示彩塑动物形象文字介绍 */}
                <motion.div variants={itemVariants}>
                  <Card variant="glass" shadow="sm">
                    <h3 className="text-base md:text-lg font-bold text-brand-accent-light mb-2 font-comic-chinese flex items-center gap-2">
                      <span aria-hidden="true">{getAnimalEmoji(data.animal)}</span>动物形象
                    </h3>
                    <p className="text-sm md:text-base text-neutral-200 leading-relaxed">
                      {data.origin_statue_feature}
                    </p>
                  </Card>
                </motion.div>
              </div>
            </motion.section>

            {/* ===== 转化按钮区 ===== */}
            <motion.section className="pb-16" variants={itemVariants} aria-labelledby="cta-heading">
              <h3 id="cta-heading" className="sr-only">获取专属纸雕艺术品</h3>
              <Button
                size="xl"
                fullWidth
                leftIcon={
                  <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                }
                onClick={handleJumpToShop}
                className="gold-button py-5 md:py-6 rounded-2xl text-lg md:text-xl font-bold shadow-gold mb-4"
                aria-label={`将${data.name}转化为纸雕艺术品，跳转至纸喜爱商店`}
              >
                带走我的本命守护纸雕
              </Button>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="secondary"
                  size="md"
                  fullWidth
                  onClick={handleShare}
                  leftIcon={
                    shareSuccess ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    )
                  }
                >
                  {shareSuccess ? '已复制链接' : '分享给朋友'}
                </Button>
                <Button
                  variant="secondary"
                  size="md"
                  fullWidth
                  onClick={() => navigate('/')}
                  leftIcon={
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  }
                >
                  重新测算
                </Button>
              </div>
              <p className="text-neutral-400 mt-6 text-sm text-center leading-relaxed">
                点击跳转至「纸喜爱纸艺」商店，将 {data.name} 化为精美纸雕艺术品
              </p>
            </motion.section>
          </div>
        </motion.div>

      {/* 全屏灯箱 — 用 100dvh 强制锚定到可见视口（避开 iOS URL bar 高度抖动） */}
      {lightboxIndex !== null && lightboxImages.length > 0 && createPortal(
        <div
          className="lightbox-backdrop result-lightbox-backdrop"
          style={{ top: `${lightboxTop}px` }}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${data.name} 大图预览`}
        >
          <button className="lightbox-close" onClick={closeLightbox} aria-label="关闭大图预览">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {lightboxIndex > 0 && (
            <button
              className="lightbox-nav lightbox-nav-prev"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }}
              aria-label="上一张"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {lightboxIndex < lightboxImages.length - 1 && (
            <button
              className="lightbox-nav lightbox-nav-next"
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }}
              aria-label="下一张"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          <div className="lightbox-title">
            {lightboxIndex === 0 ? `${data.name} · 新中式插画` : `${data.name} · 玉皇庙彩塑`}
            {lightboxImages.length > 1 && (
              <span className="lightbox-counter">{lightboxIndex + 1} / {lightboxImages.length}</span>
            )}
          </div>
          <div
            className="lightbox-zoom-container"
            onClick={(e) => e.stopPropagation()}
          >
            <GalleryZoomImage
              index={lightboxIndex}
              items={lightboxImages}
              onIndexChange={setLightboxIndex}
            />
          </div>
          <div className="lightbox-hint">双指或滚轮缩放 · 左右滑动切换</div>
        </div>,
        document.body
      )}
      </>
    </Layout>
  );
};

export default ResultPage;
